import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { apiKey } from "../../../config/config";
import { toast } from "react-toastify";
import { languageHelper } from "../../../config/helperFunctions";

function AddOfferForm() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const { id } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    offer_type: "single",
    food_menu_id: null,
    product_ids: [],
    category_id: null,
    buy: 4,
    free: 1,
    start_date: new Date().toISOString().split("T")[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    status: "active",
    specific_customers: "no",
    multi_customer_id: [],
  });

  // Fetch food menus for product selection
  useEffect(() => {
    axios
      .get(`${apiKey}/api/rms/admin/foodMenus`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          status: "active",
          catStatus: true,
          outletId: user?.user?.outlet_id,
        },
      })
      .then((res) => {
        const foodMenus = res.data?.foodMenus || [];
        const options = foodMenus.map((item) => ({
          value: item.id,
          label: `${item.name} (${user?.user?.currency || "S.R"} ${item.sale_price})`,
          name: item.name,
        }));
        setProducts(options);
      })
      .catch((err) => {
        console.error("Fetch products error:", err);
      })
      .finally(() => {
        if (!id) setPageLoading(false);
      });
  }, [user?.token, user?.user?.outlet_id, user?.user?.currency, id]);

  // Fetch categories for category offer selection
  useEffect(() => {
    axios
      .get(`${apiKey}/api/rms/admin/categories`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user?.outlet_id,
        },
      })
      .then((res) => {
        const catList = Array.isArray(res.data)
          ? res.data
          : (res.data?.data || []);
        const options = catList.map((c) => ({
          value: c.id,
          label: c.category_name,
          name: c.category_name,
        }));
        setCategories(options);
      })
      .catch((err) => {
        console.error("Fetch categories error:", err);
      });
  }, [user?.token, user?.user?.outlet_id]);

  // Fetch customers for specific customer selection
  useEffect(() => {
    axios
      .get(`${apiKey}/api/rms/admin/customer`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        const custList = Array.isArray(res.data) ? res.data : [];
        const options = custList.map((c) => ({
          value: c.id,
          label: `${c.name} ${c.phone ? `(${c.phone})` : ""}`,
        }));
        setCustomers(options);
      })
      .catch((err) => {
        console.error("Fetch customers error:", err);
      });
  }, [user?.token]);

  // Fetch offer details if editing
  useEffect(() => {
    if (!id) return;

    axios
      .get(`${apiKey}/api/rms/admin/offers/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          const offer = res.data.data;
          const pIds =
            offer.product_ids ||
            (offer.food_menus ? offer.food_menus.map((f) => f.id) : []);

          setFormData({
            title: offer.title || "",
            offer_type: offer.offer_type || (offer.category_id ? "category" : "single"),
            food_menu_id: offer.food_menu_id,
            product_ids: Array.isArray(pIds) ? pIds : [],
            category_id: offer.category_id || null,
            buy: offer.buy,
            free: offer.free,
            start_date: offer.start_date,
            end_date: offer.end_date,
            status: offer.status || "active",
            specific_customers: offer.specific_customers || "no",
            multi_customer_id:
              offer.multi_customer_id ||
              (offer.customers ? offer.customers.map((c) => c.id) : []),
          });
        }
      })
      .catch((err) => {
        toast.error("Failed to load offer details", {
          position: "bottom-right",
          theme: "colored",
        });
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [id, user?.token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedProductOption = products.find(
    (p) => p.value === formData.food_menu_id
  );

  const selectedCategoryOption = categories.find(
    (c) => c.value === formData.category_id
  );

  const handleOfferTypeChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({
      ...prev,
      offer_type: val,
      title:
        val === "category"
          ? (prev.title === "" || prev.title.startsWith("Buy ")
              ? `Buy ${prev.buy} Get ${prev.free} Free (${selectedCategoryOption?.name || "Category"})`
              : prev.title)
          : val === "group"
          ? (prev.title === "" || prev.title.startsWith("Buy ")
              ? `Buy ${prev.buy} Get ${prev.free} Free (Mix & Match)`
              : prev.title)
          : (selectedProductOption
              ? `Buy ${prev.buy} Get ${prev.free} Free (${selectedProductOption.name})`
              : prev.title),
    }));
  };

  const handleProductChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      food_menu_id: selectedOption ? selectedOption.value : null,
      title:
        prev.title === "" || prev.title.startsWith("Buy ")
          ? `Buy ${prev.buy} Get ${prev.free} Free (${selectedOption?.name || "Item"})`
          : prev.title,
    }));
  };

  const handleCategoryChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      category_id: selectedOption ? selectedOption.value : null,
      title:
        prev.title === "" || prev.title.startsWith("Buy ")
          ? `Buy ${prev.buy} Get ${prev.free} Free (${selectedOption?.name || "Category"})`
          : prev.title,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.offer_type === "single" && !formData.food_menu_id) {
      toast.error(languageHelper("Please select a product"), {
        position: "bottom-right",
        theme: "colored",
      });
      return;
    }

    if (
      formData.offer_type === "group" &&
      (!formData.product_ids || formData.product_ids.length < 2)
    ) {
      toast.error(
        languageHelper("Please select at least 2 products for a group offer"),
        {
          position: "bottom-right",
          theme: "colored",
        }
      );
      return;
    }

    if (formData.offer_type === "category" && !formData.category_id) {
      toast.error(languageHelper("Please select a category"), {
        position: "bottom-right",
        theme: "colored",
      });
      return;
    }

    if (!formData.buy || formData.buy < 1) {
      toast.error(languageHelper("Buy quantity must be at least 1"), {
        position: "bottom-right",
        theme: "colored",
      });
      return;
    }

    if (!formData.free || formData.free < 1) {
      toast.error(languageHelper("Free quantity must be at least 1"), {
        position: "bottom-right",
        theme: "colored",
      });
      return;
    }

    if (
      formData.specific_customers === "yes" &&
      (!formData.multi_customer_id || formData.multi_customer_id.length === 0)
    ) {
      toast.error(languageHelper("Please select at least one customer"), {
        position: "bottom-right",
        theme: "colored",
      });
      return;
    }

    setLoading(true);

    const payload = {
      ...formData,
      offer_type: formData.offer_type,
      food_menu_id:
        formData.offer_type === "single" ? formData.food_menu_id : null,
      product_ids:
        formData.offer_type === "group" ? formData.product_ids : [],
      category_id:
        formData.offer_type === "category" ? formData.category_id : null,
      buy: parseInt(formData.buy, 10),
      free: parseInt(formData.free, 10),
      outlet_id: user?.user?.outlet_id,
      specific_customers: formData.specific_customers,
      multi_customer_id:
        formData.specific_customers === "yes" ? formData.multi_customer_id : [],
    };

    const request = id
      ? axios.put(`${apiKey}/api/rms/admin/offers/${id}`, payload, {
          headers: { Authorization: `Bearer ${user?.token}` },
        })
      : axios.post(`${apiKey}/api/rms/admin/offers`, payload, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });

    request
      .then((res) => {
        toast.success(
          res.data?.message ||
            (id ? "Offer updated successfully!" : "Offer created successfully!"),
          {
            position: "bottom-right",
            theme: "colored",
          }
        );
        navigate(-1);
      })
      .catch((err) => {
        console.error("Submit error:", err);
        const errorMsg =
          err.response?.data?.message || "Failed to save offer.";
        toast.error(errorMsg, {
          position: "bottom-right",
          theme: "colored",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  const bundleTotal = Number(formData.buy || 0) + Number(formData.free || 0);

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-d-bg-text text-2xl">
              {languageHelper(
                id ? "Edit Offer (Buy N Get N Free)" : "Add Offer (Buy N Get N Free)"
              )}
            </h1>
          </div>
        </div>

        <div className="w-full">
          <form className="w-full" onSubmit={handleSubmit}>
            <div className="grid grid-cols-4 gap-x-8 gap-y-2 w-full">
              {/* Offer Type Radio */}
              <label htmlFor="" className="col-span-4">
                <div className="form-check text-d-bg-text">
                  <div>
                    <h2 className="text-sm mb-2">{languageHelper("Offer Type")}</h2>
                  </div>
                  <input
                    type="radio"
                    name="offer_type"
                    id="offer_type_single"
                    value="single"
                    checked={formData.offer_type === "single"}
                    onChange={handleOfferTypeChange}
                  />
                  <label
                    className="form-check-label pe-5 ps-3 cursor-pointer"
                    htmlFor="offer_type_single"
                  >
                    {languageHelper("Single Product Offer")}
                  </label>
                  <input
                    className="ps-5"
                    type="radio"
                    name="offer_type"
                    id="offer_type_group"
                    value="group"
                    checked={formData.offer_type === "group"}
                    onChange={handleOfferTypeChange}
                  />
                  <label
                    className="form-check-label pe-5 ps-3 cursor-pointer"
                    htmlFor="offer_type_group"
                  >
                    {languageHelper("Group / Mix & Match Offer")}
                  </label>
                  <input
                    className="ps-5"
                    type="radio"
                    name="offer_type"
                    id="offer_type_category"
                    value="category"
                    checked={formData.offer_type === "category"}
                    onChange={handleOfferTypeChange}
                  />
                  <label
                    className="form-check-label ps-3 cursor-pointer"
                    htmlFor="offer_type_category"
                  >
                    {languageHelper("Category Offer")}
                  </label>
                </div>
              </label>

              {/* Selection based on Offer Type */}
              {formData.offer_type === "single" ? (
                <label htmlFor="" className="col-span-2">
                  <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                    {languageHelper("Product Name")}
                  </span>
                  <div className="mb-2 pt-0">
                    <Select
                      className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                      required
                      placeholder={languageHelper("Select Product")}
                      options={products}
                      value={selectedProductOption || null}
                      onChange={handleProductChange}
                    />
                  </div>
                </label>
              ) : formData.offer_type === "group" ? (
                <label htmlFor="" className="col-span-2">
                  <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                    {languageHelper("Group Products (Select 2 or more)")}
                  </span>
                  <div className="mb-2 pt-0">
                    <Select
                      className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                      required
                      isMulti
                      closeMenuOnSelect={false}
                      placeholder={languageHelper("Select 2 or more products")}
                      options={products}
                      value={products.filter((p) =>
                        formData.product_ids?.includes(p.value)
                      )}
                      onChange={(selected) => {
                        const ids = selected ? selected.map((s) => s.value) : [];
                        setFormData((prev) => ({
                          ...prev,
                          product_ids: ids,
                          title:
                            prev.title === "" || prev.title.startsWith("Buy ")
                              ? `Buy ${prev.buy} Get ${prev.free} Free (Mix & Match)`
                              : prev.title,
                        }));
                      }}
                    />
                  </div>
                </label>
              ) : (
                <label htmlFor="" className="col-span-2">
                  <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                    {languageHelper("Category Name")}
                  </span>
                  <div className="mb-2 pt-0">
                    <Select
                      className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                      required
                      placeholder={languageHelper("Select Category")}
                      options={categories}
                      value={selectedCategoryOption || null}
                      onChange={handleCategoryChange}
                    />
                  </div>
                </label>
              )}

              {/* Offer Title */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative">
                  {languageHelper("Offer Title (Optional)")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder={languageHelper("e.g. Buy 4 Get 1 Free")}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>

              {/* Buy Quantity */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Buy Quantity (N to Pay)")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="number"
                    name="buy"
                    min="1"
                    value={formData.buy}
                    onChange={handleChange}
                    placeholder={languageHelper("e.g. 4")}
                    required
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>

              {/* Free Quantity */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Free Quantity (N Free)")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="number"
                    name="free"
                    min="1"
                    value={formData.free}
                    onChange={handleChange}
                    placeholder={languageHelper("e.g. 1")}
                    required
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>

              {/* Start Date */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Start Date")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    required
                    className="p-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>

              {/* End Date */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("End Date")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    required
                    className="p-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>

              {/* Specific Customers Radio */}
              <label htmlFor="" className="col-span-2">
                <div className="form-check text-d-bg-text">
                  <div>
                    <h2 className="text-sm mb-2">{languageHelper("Specific Customers")}</h2>
                  </div>
                  <input
                    type="radio"
                    name="specific_customers"
                    id="specific_customers_yes"
                    value="yes"
                    checked={formData.specific_customers === "yes"}
                    onChange={handleChange}
                  />
                  <label className="form-check-label pe-5 ps-3 cursor-pointer" htmlFor="specific_customers_yes">
                    {languageHelper("Yes")}
                  </label>
                  <input
                    className="ps-5"
                    type="radio"
                    name="specific_customers"
                    id="specific_customers_no"
                    value="no"
                    checked={formData.specific_customers === "no"}
                    onChange={handleChange}
                  />
                  <label className="form-check-label ps-3 cursor-pointer" htmlFor="specific_customers_no">
                    {languageHelper("No")}
                  </label>
                </div>
              </label>

              {/* Status Radio */}
              <label htmlFor="" className="col-span-2">
                <div className="form-check text-d-bg-text">
                  <div>
                    <h2 className="text-sm mb-2">{languageHelper("Status")}</h2>
                  </div>
                  <input
                    type="radio"
                    name="status"
                    id="status_active"
                    value="active"
                    checked={formData.status === "active"}
                    onChange={handleChange}
                  />
                  <label className="form-check-label pe-5 ps-3 cursor-pointer" htmlFor="status_active">
                    {languageHelper("Active")}
                  </label>
                  <input
                    type="radio"
                    name="status"
                    id="status_inactive"
                    value="inactive"
                    checked={formData.status === "inactive"}
                    onChange={handleChange}
                  />
                  <label className="form-check-label ps-3 cursor-pointer" htmlFor="status_inactive">
                    {languageHelper("Inactive")}
                  </label>
                </div>
              </label>

              {/* Customers Multi-Select */}
              {formData.specific_customers === "yes" && (
                <label htmlFor="" className="col-span-4">
                  <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                    {languageHelper("Customers")}
                  </span>
                  <div className="mb-2 pt-0">
                    <Select
                      className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                      required
                      placeholder={languageHelper("Select Customers")}
                      options={customers}
                      closeMenuOnSelect={false}
                      isMulti
                      value={customers.filter((customer) =>
                        formData.multi_customer_id?.includes(customer.value)
                      )}
                      onChange={(selected) => {
                        setFormData((prev) => ({
                          ...prev,
                          multi_customer_id: selected ? selected.map((s) => s.value) : [],
                        }));
                      }}
                    />
                  </div>
                </label>
              )}

              {/* Rule Summary Helper Banner */}
              <div className="col-span-4 mt-2 p-3 bg-white bg-opacity-10 rounded border border-d-bg text-d-bg-text text-xs flex items-center gap-2">
                <span>🎁</span>
                <span>
                  {languageHelper("Rule Preview")}:{" "}
                  {formData.offer_type === "category" ? (
                    <>
                      {languageHelper("If the customer buys any combination of")}{" "}
                      <strong>{bundleTotal}</strong>{" "}
                      {languageHelper("items from the")}{" "}
                      <strong className="text-purple-700">
                        {selectedCategoryOption?.name || languageHelper("selected")}
                      </strong>{" "}
                      {languageHelper("category")},{" "}
                      <strong>{formData.buy}</strong> {languageHelper("will be charged and the lowest-priced")}{" "}
                      <strong className="text-green-600">
                        {formData.free} {languageHelper("item(s) will be FREE")}
                      </strong>
                      .
                    </>
                  ) : formData.offer_type === "group" ? (
                    <>
                      {languageHelper("If the customer buys any combination of")}{" "}
                      <strong>{bundleTotal}</strong>{" "}
                      {languageHelper("items from the selected group")},{" "}
                      <strong>{formData.buy}</strong> {languageHelper("will be charged and the lowest-priced")}{" "}
                      <strong className="text-green-600">
                        {formData.free} {languageHelper("item(s) will be FREE")}
                      </strong>
                      .
                    </>
                  ) : (
                    <>
                      {languageHelper("If the customer buys")}{" "}
                      <strong>{bundleTotal}</strong>{" "}
                      {selectedProductOption?.name || languageHelper("items")},{" "}
                      <strong>{formData.buy}</strong> {languageHelper("will be charged and")}{" "}
                      <strong className="text-green-600">
                        {formData.free} {languageHelper("will be FREE")}
                      </strong>
                      .
                    </>
                  )}
                  {formData.specific_customers === "yes" && (
                    <span className="ml-1 text-purple-700 font-semibold">
                      ({languageHelper("Applies only to selected customers")})
                    </span>
                  )}
                </span>
              </div>
            </div>

            <span className="w-full bg-d-bg h-1 my-2 inline-block"></span>
            <div className="flex gap-2">
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit relative"
                type="submit"
                disabled={loading}
              >
                {loading && (
                  <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50 z-10">
                    <img
                      className="w-4 mx-auto"
                      src="/assets/gif/button_loader.gif"
                      alt=""
                    />
                  </span>
                )}
                {languageHelper("Submit")}
              </button>
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="button"
                onClick={() => navigate(-1)}
              >
                {languageHelper("Back")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default AddOfferForm;
